import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { FileCheck2, ClipboardCheck, Calculator, Download, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { MarketingNav, MarketingFooter, useSeoMeta } from "@/components/MarketingChrome";

async function downloadMagnet(endpoint, payload, filename) {
  const { data } = await api.post(endpoint, payload, { responseType: "blob" });
  const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function MagnetCard({ icon: Icon, testid, title, desc, bullets, onSubmit, cta, children }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await onSubmit(email);
      toast.success("Downloaded — check your downloads folder.");
      setEmail("");
    } catch (err) {
      toast.error("Please enter a valid work email and try again.");
    } finally { setBusy(false); }
  };
  return (
    <div className="card-elevated rounded-sm p-8 flex flex-col" data-testid={testid}>
      <div className="h-11 w-11 rounded-sm bg-[hsl(var(--chart-2))]/10 border border-[hsl(var(--chart-2))]/20 flex items-center justify-center">
        <Icon className="h-5 w-5 text-[hsl(var(--chart-2))]" strokeWidth={1.5} />
      </div>
      <h3 className="font-heading text-xl font-bold mt-6 tracking-tight">{title}</h3>
      <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{desc}</p>
      {bullets && (
        <ul className="mt-5 space-y-2 flex-1">
          {bullets.map((b) => (
            <li key={b} className="flex items-start gap-2 text-sm text-muted-foreground">
              <Check className="h-4 w-4 text-[hsl(var(--chart-2))] shrink-0 mt-0.5" strokeWidth={2.5} /> {b}
            </li>
          ))}
        </ul>
      )}
      {children}
      <form onSubmit={submit} className="mt-6">
        <Input required type="email" placeholder="Work email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid={`${testid}-email`} className="rounded-sm bg-white/5 border-white/10 focus:border-primary h-11 mb-3" />
        <Button disabled={busy} data-testid={`${testid}-submit`} className="w-full rounded-sm bg-[hsl(var(--chart-2))] hover:bg-[hsl(var(--chart-2))]/90 text-black h-11 font-medium glow-green">
          <Download className="h-4 w-4 mr-1" strokeWidth={1.5} /> {busy ? "Preparing…" : cta}
        </Button>
      </form>
    </div>
  );
}

export default function Resources() {
  useSeoMeta(
    "Free Pharma Compliance Resources — Vaultvec",
    "Free GxP compliance tools: a 21 CFR Part 11 readiness checklist, an ALCOA+ data-integrity template, and a GxP RPO/RTO backup calculator."
  );
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const [rpo, setRpo] = useState(24);
  const [rto, setRto] = useState(8);
  const [gb, setGb] = useState(50);
  const [systems, setSystems] = useState(10);
  const [crit, setCrit] = useState("high");

  const preview = useMemo(() => {
    const freq = Math.max(1, Math.round(rpo));
    return {
      freq,
      points: Math.round((24 / freq) * 10) / 10,
      monthly: Math.round(gb * 30 * 10) / 10,
      risk: rpo <= 4 && rto <= 4 ? "Well-controlled" : (rpo > 48 || rto > 48) ? "High" : (rpo > 24 || rto > 24) ? "Elevated" : "Low",
    };
  }, [rpo, rto, gb]);

  return (
    <div className="relative min-h-screen bg-background text-foreground">
      <div className="noise-overlay" />
      <MarketingNav />

      <section className="relative pt-32 pb-16 max-w-7xl mx-auto px-6 overflow-hidden">
        <div className="ambient-spot top-10 right-0 w-[420px] h-[320px] opacity-40" />
        <span className="eyebrow-line font-mono text-xs tracking-[0.2em] uppercase text-[hsl(var(--chart-2))]">Free Resources</span>
        <h1 className="font-heading font-extrabold tracking-tighter text-4xl sm:text-5xl lg:text-6xl mt-5 max-w-3xl leading-[0.95]">
          Compliance tools your team can use today.
        </h1>
        <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-2xl leading-relaxed">
          Practical, inspector-aligned resources for regulated pharma teams — no fluff. Grab the ones you need; we'll only follow up if you ask.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-28 grid md:grid-cols-3 gap-6 items-start">
        <MagnetCard
          icon={ClipboardCheck}
          testid="magnet-checklist"
          title="21 CFR Part 11 Readiness Checklist"
          desc="A self-assessment of the exact electronic-records controls FDA inspectors look for."
          bullets={["ALCOA+ electronic records", "Audit trails & e-signatures", "Access control & validation"]}
          cta="Download the Checklist"
          onSubmit={(email) => downloadMagnet("/resources/compliance-checklist", { email }, "vaultvec-21cfr-part11-checklist.pdf")}
        />

        <MagnetCard
          icon={FileCheck2}
          testid="magnet-alcoa"
          title="ALCOA+ Data Integrity Template"
          desc="The nine ALCOA+ principles with definitions and how to evidence each in an inspection."
          bullets={["Attributable · Legible · Contemporaneous", "Original · Accurate · Complete", "Consistent · Enduring · Available"]}
          cta="Download the Template"
          onSubmit={(email) => downloadMagnet("/resources/alcoa-template", { email }, "vaultvec-alcoa-plus-template.pdf")}
        />

        <MagnetCard
          icon={Calculator}
          testid="magnet-rporto"
          title="GxP RPO / RTO Calculator"
          desc="Enter your recovery objectives to get a recommended backup strategy and a branded PDF summary."
          cta="Get my Recovery Summary"
          onSubmit={(email) => downloadMagnet("/resources/rpo-rto-summary", { email, rpo_hours: Number(rpo), rto_hours: Number(rto), data_change_gb: Number(gb), systems: Number(systems), criticality: crit }, "vaultvec-rpo-rto-summary.pdf")}
        >
          <div className="mt-5 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <label className="text-xs text-muted-foreground">RPO (hours)
                <Input type="number" min="1" value={rpo} onChange={(e) => setRpo(e.target.value)} data-testid="rporto-rpo" className="rounded-sm bg-white/5 border-white/10 mt-1.5 h-10" />
              </label>
              <label className="text-xs text-muted-foreground">RTO (hours)
                <Input type="number" min="1" value={rto} onChange={(e) => setRto(e.target.value)} data-testid="rporto-rto" className="rounded-sm bg-white/5 border-white/10 mt-1.5 h-10" />
              </label>
              <label className="text-xs text-muted-foreground">Daily data change (GB)
                <Input type="number" min="1" value={gb} onChange={(e) => setGb(e.target.value)} data-testid="rporto-gb" className="rounded-sm bg-white/5 border-white/10 mt-1.5 h-10" />
              </label>
              <label className="text-xs text-muted-foreground">Systems
                <Input type="number" min="1" value={systems} onChange={(e) => setSystems(e.target.value)} data-testid="rporto-systems" className="rounded-sm bg-white/5 border-white/10 mt-1.5 h-10" />
              </label>
            </div>
            <div className="rounded-sm bg-black/40 border border-border p-4 font-mono text-[11px] space-y-1.5" data-testid="rporto-preview">
              <div className="flex justify-between"><span className="text-muted-foreground">Backup frequency</span><span>every {preview.freq}h</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Restore points/day</span><span>{preview.points}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Est. storage/month</span><span>{preview.monthly} GB</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Recovery posture</span><span className="text-[hsl(var(--chart-2))]">{preview.risk}</span></div>
            </div>
          </div>
        </MagnetCard>
      </section>

      <MarketingFooter />
    </div>
  );
}
